import React, {
  createContext,
  useReducer,
  useCallback,
  useEffect,
  useContext,
  useMemo,
  ReactNode,
} from 'react';

import * as Sentry from '@sentry/nextjs';
import API_KEY_NAME from 'constants/apiTokenValue';
import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  signInAnonymously,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  FacebookAuthProvider,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  EmailAuthProvider,
} from 'firebase/auth';
import type { User, UserCredential, AuthCredential, Auth } from 'firebase/auth';
import captureExceptionForClientSide from 'utils/captureExceptionForClientSide';
import processErrorWithGoogleTag from 'utils/processErrorWithGoogleTag';
import processGoogleTag from 'utils/processGoogleTag';

import { IError, Error } from 'models/error';
import { IUser } from 'models/user';
import { IUserProfile } from 'models/userProfile';
import { HttpRequestError } from 'services/httpRequestError';
import { fetcher } from 'services/httpRequestor';
import { UserService } from 'services/userService';

import cookie from '../utils/cookie';
import reducer, { initialState, AuthState, AuthTypes } from './state/authState';

// Initialize Firebase - handle both client and server
let auth: Auth | null = null;
if (typeof window !== 'undefined') {
  const firebaseConfig = ENV.FIREBASE_WEB_SDK_CONFIG;
  if (firebaseConfig) {
    if (!getApps().length) {
      initializeApp(firebaseConfig);
    }
    try {
      auth = getAuth();
    } catch {
      console.warn('Firebase disabled - invalid configuration');
    }
  }
}

interface IUserSignIn {
  email: string;
  password: string;
}

interface IUserResetPassword {
  email: string;
}

export interface IActions {
  loginAnonymous: () => Promise<UserCredential | null>;
  login: (user: IUserSignIn) => Promise<boolean>;
  loginFacebook: () => Promise<boolean>;
  loginGoogle: () => Promise<boolean>;
  createUser: (user: IUserProfile, recaptchaToken: string) => Promise<boolean>;
  updateUser: (data: any, recaptchaToken: string) => Promise<boolean>;
  logout: () => Promise<void>;
  initialise: () => void;
  resetPassword: (user: IUserResetPassword) => Promise<boolean>;
  fetchAuthorisedUser: () => Promise<IUser | null>;
  linkAccount: (credentials: AuthCredential) => Promise<void>;
  linkEmailAccount: (email: string, password: string) => Promise<void>;
  clearError: () => void;
}

const processLoginSuccessTag = (
  authenticationMethod: string,
  userUID: string,
) =>
  processGoogleTag({
    event: 'login',
    authenticationMethod,
    userUID,
  });

interface AuthContextValue {
  state: AuthState;
  actions: IActions;
}

const defaultAuthActions: IActions = {
  fetchAuthorisedUser: () =>
    Promise.reject(new Error('AuthContext not initialized')),
  loginAnonymous: () =>
    Promise.reject(new Error('AuthContext not initialized')),
  createUser: () => Promise.reject(new Error('AuthContext not initialized')),
  loginFacebook: () => Promise.reject(new Error('AuthContext not initialized')),
  loginGoogle: () => Promise.reject(new Error('AuthContext not initialized')),
  login: () => Promise.reject(new Error('AuthContext not initialized')),
  logout: () => Promise.reject(new Error('AuthContext not initialized')),
  initialise: () => undefined,
  resetPassword: () => Promise.reject(new Error('AuthContext not initialized')),
  updateUser: () => Promise.reject(new Error('AuthContext not initialized')),
  linkAccount: () => Promise.reject(new Error('AuthContext not initialized')),
  linkEmailAccount: () =>
    Promise.reject(new Error('AuthContext not initialized')),
  clearError: () => undefined,
};

export const AuthContext = createContext<AuthContextValue>({
  state: initialState,
  actions: defaultAuthActions,
});

interface AuthContextProviderProps {
  children: ReactNode;
  serverInitialState?: Partial<AuthState>;
}

function AuthContextProvider({
  serverInitialState = {},
  children,
}: AuthContextProviderProps) {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    ...serverInitialState,
  });

  const clearError = useCallback(() => {
    dispatch({ type: AuthTypes.AUTH_CLEAR_ERROR });
  }, []);

  const parseErrorMessage = (error: IError) => {
    switch (error.code) {
      case 'auth/user-not-found':
        return (
          <>
            There has been an issue logging into your account. Please check your
            email and password. If you don't have an account{' '}
            <a href="/signin?createNewAccountTabActive=true">
              you can create one here
            </a>
          </>
        );
      default:
        return error.message;
    }
  };

  const dispatchAndCaptureAuthError = useCallback(
    (error: IError | HttpRequestError, captureContext?: any) => {
      const firebaseError = error as IError;
      captureExceptionForClientSide(error, captureContext);

      if (firebaseError?.code) {
        const { code } = firebaseError;
        const message = parseErrorMessage(firebaseError);

        dispatch({
          type: AuthTypes.AUTH_FAILURE,
          payload: { code, message } as IError,
        });
        return;
      }

      const { status, data } = error as HttpRequestError;
      if (status && data.error) {
        dispatch({
          type: AuthTypes.AUTH_FAILURE,
          payload: { code: status, message: data.error },
        });
        return;
      }

      dispatch({
        type: AuthTypes.AUTH_FAILURE,
        payload: { code: 400, message: 'unexpected error' },
      });
    },
    [],
  );

  const handleLoginError = useCallback(
    (error: IError | HttpRequestError, authenticationMethod: string) => {
      processErrorWithGoogleTag('login error', error.toString(), {
        authenticationMethod,
      });
      dispatchAndCaptureAuthError(error);
    },
    [dispatchAndCaptureAuthError],
  );

  const createFromSocial = useCallback(
    (userCredential: UserCredential, puid?: string) => {
      const { user } = userCredential;
      const credential = (userCredential as any)?.credential;
      const additionalUserInfo = (userCredential as any)?.additionalUserInfo;

      const payload = {
        displayName: user.displayName,
        email: user.email,
        uid: user.uid,
        provider: credential?.providerId || additionalUserInfo?.providerId,
        puid,
      };

      const userService = new UserService(fetcher());

      // Save user information on Stack9
      return userService.social(payload).then(({ status, data }) => {
        if (status >= 400) {
          dispatchAndCaptureAuthError(new Error((data as any).message));
          return false;
        }

        return true;
      });
    },
    [dispatchAndCaptureAuthError],
  );

  const onUserAuthChanged = useCallback((user: User | null) => {
    if (user) {
      return user.getIdToken().then(value => {
        cookie.set(API_KEY_NAME, value, { path: '/', sameSite: true });

        const userService = new UserService(fetcher());
        userService.getAuthorisedUser().then(({ data }) => {
          Sentry.setUser({ email: data?.email, id: data?.uid });

          dispatch({
            type: AuthTypes.LOGIN_SUCCESS,
            payload: data,
          });
        });
      });
    }

    cookie.remove(API_KEY_NAME);
    dispatch({ type: AuthTypes.SIGNOUT_SUCCESS });

    return undefined;
  }, []);

  useEffect(() => {
    // Only set up auth listener on client side
    if (!auth) return undefined;

    return onAuthStateChanged(auth, onUserAuthChanged);
  }, [onUserAuthChanged]);

  const loginAnonymous = useCallback(async () => {
    if (!auth) return null;

    try {
      return await signInAnonymously(auth);
    } catch (error: any) {
      handleLoginError(error, 'anonymous');
    }

    return null;
  }, [handleLoginError]);

  const loginFacebook = useCallback(async () => {
    if (!auth) return false;

    try {
      const provider = new FacebookAuthProvider();
      dispatch({
        type: AuthTypes.LOGIN,
      });
      return await signInWithPopup(auth, provider).then(authResponse =>
        createFromSocial(authResponse, state.user?.uid),
      );
    } catch (error: any) {
      handleLoginError(error, 'facebook');
    }

    return false;
  }, [handleLoginError, state.user?.uid, createFromSocial]);

  const loginGoogle = useCallback(async () => {
    if (!auth) return false;

    try {
      const provider = new GoogleAuthProvider();
      dispatch({
        type: AuthTypes.LOGIN,
      });
      return await signInWithPopup(auth, provider).then(authResponse =>
        createFromSocial(authResponse, state.user?.uid),
      );
    } catch (error: any) {
      handleLoginError(error, 'google');
    }

    return false;
  }, [handleLoginError, state.user?.uid, createFromSocial]);

  const login = useCallback(
    async (user: IUserSignIn) => {
      if (!auth) return false;

      try {
        dispatch({
          type: AuthTypes.LOGIN,
        });
        const created = await signInWithEmailAndPassword(
          auth,
          user.email,
          user.password,
        ).then(authResponse => createFromSocial(authResponse, state.user?.uid));
        if (state.user?.uid) {
          processLoginSuccessTag('email', state.user?.uid);
        }
        return created;
      } catch (error: any) {
        processErrorWithGoogleTag('login', error.toString());
        dispatchAndCaptureAuthError(error);
      }

      return false;
    },
    [state.user?.uid, createFromSocial, dispatchAndCaptureAuthError],
  );

  const updateUser = useCallback(
    async (toBeUpdatedProps: any, recaptchaToken: string) => {
      try {
        dispatch({
          type: AuthTypes.UPDATE_USER,
        });
        const userService = new UserService(fetcher());

        await userService.update(toBeUpdatedProps, recaptchaToken);

        dispatch({
          type: AuthTypes.UPDATE_USER_SUCCESS,
        });
        return true;
      } catch (error) {
        dispatch({
          type: AuthTypes.UPDATE_USER_FAILURE,
          payload: error,
        });
        return false;
      }
    },
    [],
  );

  const fetchAuthorisedUser = useCallback(async () => {
    try {
      dispatch({
        type: AuthTypes.LOGIN,
      });

      const userService = new UserService(fetcher());
      const { data } = await userService.getAuthorisedUser();
      dispatch({
        type: AuthTypes.LOGIN_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error: any) {
      dispatchAndCaptureAuthError(error);
    }

    return null;
  }, [dispatchAndCaptureAuthError]);

  const createUser = useCallback(
    async (userProfile: IUserProfile, recaptchaToken: string) => {
      try {
        dispatch({
          type: AuthTypes.CREATE_USER,
        });

        const userService = new UserService(fetcher());
        await userService.create(userProfile, recaptchaToken);
        dispatch({
          type: AuthTypes.CREATE_USER_SUCCESS,
        });
        processGoogleTag({
          event: 'registration',
          authenticationMethod: 'email',
          userUID: state.user?.uid,
        });
        return true;
      } catch (error: any) {
        processErrorWithGoogleTag('registration', error.toString());
        dispatchAndCaptureAuthError(error);
      }

      return false;
    },
    [dispatchAndCaptureAuthError, state.user?.uid],
  );

  const logout = useCallback(async () => {
    if (!auth) return;
    await signOut(auth);
  }, []);

  const initialise = useCallback(() => {
    dispatch({ type: AuthTypes.AUTH_INITIALISED });
  }, []);

  const resetPassword = useCallback(
    async (user: IUserResetPassword) => {
      if (!auth) return false;

      clearError();

      const url = new URL(window.location.href);
      const baseUrl = `${url.protocol}//${url.host}`;

      try {
        await sendPasswordResetEmail(auth, user.email, {
          url: `${baseUrl}/signin`,
        });
        processGoogleTag({
          event: 'reset password',
          userUID: state?.user?.uid,
        });
        return true;
      } catch (error: any) {
        processErrorWithGoogleTag('reset password', error.toString());
        dispatchAndCaptureAuthError(error);
      }
      return false;
    },
    [clearError, state?.user?.uid, dispatchAndCaptureAuthError],
  );

  const linkAccount = useCallback(
    async (credentials: AuthCredential): Promise<void> => {
      if (!auth) throw new Error('Auth not initialized');

      try {
        const { currentUser } = auth;
        if (!currentUser) {
          throw new Error('No current user');
        }

        const { linkWithCredential } = await import('firebase/auth');
        const response = await linkWithCredential(currentUser, credentials);
        return onUserAuthChanged(response.user);
      } catch (error: any) {
        dispatchAndCaptureAuthError(error, {
          extra: {
            currentUser: auth?.currentUser,
          },
        });
        throw error;
      }
    },
    [onUserAuthChanged, dispatchAndCaptureAuthError],
  );

  const linkEmailAccount = useCallback(
    async (email: string, password: string) => {
      const credentials = EmailAuthProvider.credential(email, password);
      return linkAccount(credentials);
    },
    [linkAccount],
  );

  // Memoize actions object to prevent unnecessary re-renders
  const actions: IActions = useMemo(
    () => ({
      loginAnonymous,
      login,
      loginFacebook,
      loginGoogle,
      createUser,
      logout,
      initialise,
      resetPassword,
      fetchAuthorisedUser,
      updateUser,
      linkAccount,
      linkEmailAccount,
      clearError,
    }),
    [
      loginAnonymous,
      login,
      loginFacebook,
      loginGoogle,
      createUser,
      logout,
      initialise,
      resetPassword,
      fetchAuthorisedUser,
      updateUser,
      linkAccount,
      linkEmailAccount,
      clearError,
    ],
  );

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      state,
      actions,
    }),
    [state, actions],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

const useAuth = (): IActions & AuthState => {
  const { state, actions } = useContext(AuthContext);

  if (!state || !actions) {
    throw new Error('useAuth must be used within AuthContextProvider');
  }

  return { ...actions, ...state };
};

export { useAuth };

export default AuthContextProvider;
